import { Box, ComboBox, FilterChip } from "@dealer-center/core-components";
import type { InterstateOnChangeEvent } from "@dealer-center/core-components";
import { memo, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import { useOutletContext } from "react-router-dom";
import type { AccountEntityExtended } from "@dealer-center/ddc-api-account-interface";
import type { IFranchiseSelectorData } from "@dealer-center/ddc-pages-franchise-management-interface";

// Styled components for visual presentation
const StyledChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

// Styled franchise tag chip that matches the design in the image
const StyledChip = styled(FilterChip)`
  &&& {
    background-color: var(--ids-color-primary-lightest, #e6f7ff);
    border: 1px solid var(--ids-color-primary-lightest, #e6f7ff);
    color: var(--ids-color-primary-dark, #005a9e);
    font-size: 12px;
    font-weight: 500;
    border-radius: 5px;
    padding: 2px 8px;
    height: auto;
    min-height: 24px;

    .MuiChip-deleteIcon {
      color: var(--ids-color-primary-dark, #005a9e);
      &:hover {
        color: var(--ids-color-primary-darker, #004578);
      }
    }
  }
`;

// Read-only chip for view mode without delete icon
const ReadOnlyStyledChip = styled(FilterChip)`
  &&& {
    background-color: var(--ids-color-primary-lightest, #e6f7ff);
    border: 1px solid var(--ids-color-primary-lightest, #e6f7ff);
    color: var(--ids-color-primary-dark, #005a9e);
    font-size: 12px;
    font-weight: 500;
    border-radius: 5px;
    padding: 2px 8px;
    height: auto;
    min-height: 24px;

    .MuiChip-deleteIcon {
      display: none;
    }
  }
`;

// Define proper interface for selected options display items
export interface IFranchiseOption {
  label: string;
  value: string;
}

export interface IAccountFranchiseSelectorProps {
  selectedFranchises: IFranchiseSelectorData[];
  availableFranchises: IFranchiseSelectorData[];
  setSelectedFranchises: (franchises: IFranchiseSelectorData[]) => void;
  isEdit?: boolean;
  isLoading?: boolean;
  isAccountFranchisesError?: boolean;
}

export const AccountFranchiseSelector = memo<IAccountFranchiseSelectorProps>(
  ({
    selectedFranchises,
    availableFranchises,
    setSelectedFranchises,
    isEdit = false,
    isLoading = false,
    isAccountFranchisesError = false
  }: IAccountFranchiseSelectorProps) => {
    const { "css.franchise-management.enable": enableFranchiseManagement } =
      useCC9Flags();
    const { accountEntity } = useOutletContext<{
      accountEntity: AccountEntityExtended;
    }>();

    const { franchiseOptions, franchiseMap } = useMemo(() => {
      const options = availableFranchises.map(franchise => ({
        label: franchise.name,
        value: franchise.id
      }));

      const map = new Map(
        availableFranchises.map(franchise => [
          franchise.id,
          {
            id: franchise.id,
            name: franchise.name,
            abbreviation: franchise.abbreviation
          }
        ])
      );

      return { franchiseOptions: options, franchiseMap: map };
    }, [availableFranchises]);

    const handleSelect = useCallback(
      (event: InterstateOnChangeEvent<any>) => {
        const selectedValues = event.target.value || [];

        const selected: IFranchiseSelectorData[] = [];

        for (const selectedValue of selectedValues) {
          const franchiseData = franchiseMap.get(selectedValue.value);
          if (franchiseData) {
            selected.push({
              id: franchiseData.id,
              name: franchiseData.name,
              abbreviation: franchiseData.abbreviation
            });
          }
        }

        setSelectedFranchises(selected);
      },
      [franchiseMap, setSelectedFranchises]
    );

    const selectedOptionsDisplay = useMemo(() => {
      return selectedFranchises.map(f => ({
        label: f.name,
        value: f.id
      }));
    }, [selectedFranchises]);

    const handleRemove = useCallback(
      (franchiseToRemove: IFranchiseSelectorData) => {
        const newSelection = selectedFranchises.filter(
          f => f.id !== franchiseToRemove.id
        );
        setSelectedFranchises(newSelection);
      },
      [selectedFranchises, setSelectedFranchises]
    );

    // Loading state
    if (isLoading) {
      return (
        <Box sx={{ fontSize: "14px", color: "text.secondary" }}>
          Loading franchises...
        </Box>
      );
    }

    if (isAccountFranchisesError) {
      return (
        <Box sx={{ fontSize: "14px", color: "text.secondary" }}>
          Error loading account franchises
        </Box>
      );
    }

    // View mode (read-only)
    if (!isEdit) {
      const franchises = enableFranchiseManagement
        ? selectedFranchises
        : (accountEntity?.franchiseList || []).map(name => ({
            id: name,
            name
          }));

      if (franchises.length === 0) {
        return (
          <Box sx={{ fontSize: "14px", color: "text.secondary" }}>
            No franchise available
          </Box>
        );
      }

      return (
        <StyledChipsContainer>
          {franchises.map(franchise => (
            <ReadOnlyStyledChip
              disabled={true}
              key={franchise.id}
              label={franchise.name}
            />
          ))}
        </StyledChipsContainer>
      );
    }

    // Edit mode
    return (
      <Box>
        <ComboBox
          autoComplete="on"
          multiple={true}
          options={franchiseOptions}
          placeholder="Add franchises"
          size="small"
          sx={{
            ".ids-combo-box-multi-select-input": {
              ".MuiChip-root": {
                display: "none"
              }
            }
          }}
          value={selectedOptionsDisplay}
          onChange={handleSelect}
        />

        {selectedFranchises.length > 0 && (
          <StyledChipsContainer>
            {selectedFranchises.map(franchise => (
              <StyledChip
                key={franchise.id}
                label={franchise.name}
                onClick={() => handleRemove(franchise)}
              />
            ))}
          </StyledChipsContainer>
        )}
      </Box>
    );
  }
);
